// =============================================================================
// useCanFixPalletBilling — may this person open Unbilled pallets
// (/pallets/uncoded) and see Home's "N pallets need a billing code"?
//
// The pallet page's Edit rule (PalletDetail: isManagerOrAbove) with the
// custom-role arm the house pairs it with everywhere else
// (`|| can('<module>', 'manage')`). F09 R1, approved by Jason 1 Oct 2026.
//
// Why "has Pallets" is not enough:
//   • the page re-codes the billing of up to 1 000 pallets in one save, while
//     the pallet page's Edit — which can also change the pack type — is
//     managers only. Every role with Pallets (scale operator, QC technician,
//     loader, dispatcher, inspector) could do in bulk what it may not do once.
//   • both surfaces used to judge "unbilled" against financial_products, which
//     RLS (fin_products_read_scoped, last re-emitted in mig 20260710200000)
//     lets only a platform admin, a get_user_role() of ADMIN/MANAGER or a
//     tenant ADMIN (current_tenant_role()) read. For anyone else that read
//     comes back EMPTY, not refused: get_inventory_pulse (SECURITY INVOKER)
//     then counted every live pallet as unbilled on Home, and on the page the
//     empty code set left the pallet read disabled, so it showed a green
//     "All clear" it never checked.
//
// Closed server-side by mig 20261066000000 (F09 S1b): both surfaces now take
// the business's ACTIVE codes from active_billing_product_codes() (DEFINER,
// codes only), so the three seats where this rule and that policy disagree
// keep the page and the alert AND see the true figure:
//   • a CUSTOM role holding Pallets manage (approved to keep both) whose global
//     users.role — what get_user_role() reads; it never consults has_perm() —
//     is not ADMIN/MANAGER, and who is not tenant ADMIN;
//   • a tenant MANAGER (me.role is the tenant role) whose global users.role is
//     not ADMIN/MANAGER — the policy's tenant arm names ADMIN only;
//   • a FARM_MANAGER / FARM_ADMIN seat on a packhouse tenant: isManagerOrAbove
//     admits both, the policy names neither.
// (SUPER_ADMIN was never one: get_user_role() maps it to ADMIN, mig 151.)
// The rate card itself (names, variants, prices) stays behind that policy.
//
// ⚠️ SQL twin: public.can_fix_pallet_billing() (same mig, F09-NARROW, Jason
// 5 Oct) is THIS rule server-side — seat role manager-or-above
// (current_tenant_role()), users.role SUPER_ADMIN, or has_perm('pallets',
// 'manage'), plus platform mode and a live support view. The code lookup
// answers only those seats and get_inventory_pulse gives everyone else
// unbilledPallets = null. Change one, change the other, or a seat this hook
// admits gets no codes (page: every pallet unbilled) or a refused one gets them.
//
// null while me / the permission map are still loading — the route gate's
// own isPending rule — so a caller renders nothing yet instead of flashing a
// refusal at a manager whose map is a beat behind.
// =============================================================================

import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { isManagerOrAbove } from '@/lib/roles'
import type { PermAction } from '@/lib/permissions'
import type { UserRole } from '@/types/database'

/** The rule, pure. `can` is usePermissions().can. */
export function canFixPalletBilling(
  role: UserRole | null | undefined,
  can: (module: string, action: PermAction) => boolean,
): boolean {
  return isManagerOrAbove(role) || can('pallets', 'manage')
}

export function useCanFixPalletBilling(): boolean | null {
  const { data: me, isPending: mePending } = useCurrentUser()
  const { can, isPending: permsPending } = usePermissions()
  if (mePending || permsPending) return null
  return canFixPalletBilling(me?.role, can)
}
